import type { RichText } from '@fruitful/types';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import {
    BodyLargeEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    ScrollView,
    Spinner,
    useToastController,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import { RichTextEditor } from '../../../common/components/RichTextEditor';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { IS_DEV } from '../../../config/FruitfulEnvironment';
import { adminUserAtom, selectedMembershipAtom } from '../../entityAtoms';
import { useUpsertMembershipGuidanceNotesAndRefetch } from '../hooks/useUpsertMembershipGuidanceNotes';

export const membershipGuidanceNotesModalVisibleAtom = atom<boolean>(false);

export interface MembershipGuidanceNotesModalProps {
    membershipId?: string;
}

const EDITOR_HEIGHT = 24 * 24;

const parseNotes = (raw: string | null | undefined): RichText | undefined => {
    if (raw == null) return undefined;
    try {
        return JSON.parse(raw) as RichText;
    } catch {
        return raw;
    }
};

export const MembershipGuidanceNotesModal = ({
    membershipId,
}: MembershipGuidanceNotesModalProps) => {
    const [isVisible, setIsVisible] = useAtom(
        membershipGuidanceNotesModalVisibleAtom,
    );
    const adminUser = useAtomValue(adminUserAtom);
    const membership = useAtomValue(selectedMembershipAtom);

    const toast = useToastController();
    const { upsertMembershipGuidanceNotesAsync, isPending } =
        useUpsertMembershipGuidanceNotesAndRefetch(membership);

    const canView =
        adminUser.authorization.selectedMembership.notes.canView || IS_DEV;
    const canEdit =
        adminUser.authorization.selectedMembership.notes.canEdit || IS_DEV;

    const initialValue = useMemo(
        () => parseNotes(membership?.membershipGuidanceNotes?.notes),
        [membership?.membershipGuidanceNotes?.notes],
    );

    const [notes, setNotes] = useState<RichText | undefined>(initialValue);

    // Re-sync when the underlying membership (or its saved notes) changes.
    useEffect(() => {
        setNotes(initialValue);
    }, [initialValue, membershipId]);

    const handleSave = async () => {
        if (membershipId == null || !canEdit) return;

        try {
            await upsertMembershipGuidanceNotesAsync({
                membershipId,
                notes: JSON.stringify(notes),
            });
            toast.show('Notes saved', MutationSuccessToastOptions);
            setIsVisible(false);
        } catch (e) {
            toast.show('Failed to save notes', MutationErrorToastOptions());
        }
    };

    if (!canView) return null;

    const isReady = membership != null && membership.id === membershipId;

    const handleCancel = () => {
        setIsVisible(false);
    };

    return (
        <GroveSimpleModal
            isOpen={isVisible}
            onClose={handleCancel}
            hasCloseButton
        >
            <ScrollView>
                <YStack width={1200} margin={16} gap={16}>
                    <BodyLargeEmphasized>Membership notes</BodyLargeEmphasized>
                    {isReady ? (
                        <RichTextEditor
                            placeholder="Membership notes..."
                            initial={notes}
                            disabled={!canEdit}
                            height={EDITOR_HEIGHT}
                            onChange={setNotes}
                        />
                    ) : (
                        <YStack
                            height={EDITOR_HEIGHT}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Spinner />
                        </YStack>
                    )}
                    {isPending ? (
                        <XStack gap={16} alignItems="center">
                            <Spinner />
                        </XStack>
                    ) : (
                        <XStack gap={16} alignItems="center">
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.SMALL}
                                text="Cancel"
                                icon={XIcon}
                                buttonProps={{ flexShrink: 1 }}
                                onPress={handleCancel}
                            />
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                tone="positive"
                                text="Save"
                                icon={CheckIcon}
                                disabled={!canEdit || membershipId == null}
                                buttonProps={{ flexShrink: 1 }}
                                onPress={handleSave}
                            />
                        </XStack>
                    )}
                </YStack>
            </ScrollView>
        </GroveSimpleModal>
    );
};
